import React from 'react';
import styled, { css } from 'styled-components';
import { colors } from 'shared/themes';
import { TabValue } from 'src/checkout/components/sections/payment/shared/context';
import {
  Badge,
  BadgeContent,
  BadgeLabel,
  TabText,
  TabTextGroup,
} from 'src/checkout/components/sections/payment/selection/payment-tabs/payment-tabs.styles';
import { BASE_MAX_WIDTH, PLAID_OFFSET } from 'src/payments/constants';

const EMBEDDED_MAX_WIDTH = PLAID_OFFSET + BASE_MAX_WIDTH;

export type TabOption = {
  value: TabValue;
  label: string;
  sublabel?: string;
  badgeLabel: string;
  badgeIcon?: React.ReactNode;
  hasError?: boolean;
};

type PaymentTabsProps = {
  children: React.ReactNode;
  onTabChange: (tab: string) => void;
  selectedTab: TabValue;
  tabOptions: TabOption[];
  showFeeBadgeInline?: boolean;
  showEmbeddedSearch?: boolean;
};

const FeeBadge = ({ badgeLabel, badgeIcon }: { badgeLabel: string; badgeIcon?: React.ReactNode }): JSX.Element => (
  <Badge>
    <BadgeContent>
      {!!badgeIcon && badgeIcon}
      <BadgeLabel>{badgeLabel}</BadgeLabel>
    </BadgeContent>
  </Badge>
);

export const PaymentTabs: React.FC<PaymentTabsProps> = ({
  selectedTab,
  onTabChange,
  children,
  tabOptions,
  showFeeBadgeInline = false,
  showEmbeddedSearch = false,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = tabOptions.findIndex((tab) => tab.value === selectedTab);
    const totalTabs = tabOptions.length;

    const keyMap: Partial<Record<string, number>> = {
      arrowright: (currentIndex + 1) % totalTabs,
      arrowleft: (currentIndex - 1 + totalTabs) % totalTabs,
      tab: currentIndex < totalTabs - 1 ? currentIndex + 1 : currentIndex,
    };

    const newIndex = keyMap[e.key.toLowerCase()];
    if (newIndex !== undefined && newIndex !== currentIndex) {
      if (e.key !== 'Tab') {
        e.preventDefault();
      }
      onTabChange(tabOptions[newIndex].value);
    }
  };

  const firstTabValue = tabOptions.length > 0 ? tabOptions[0].value : '';

  return (
    <TabsContainer data-testid='payment-tabs-v2' showEmbeddedSearch={showEmbeddedSearch}>
      <TabHeader role='tablist'>
        {tabOptions.map(({ value, label, sublabel, badgeLabel, badgeIcon, hasError }) => {
          const selected = selectedTab === value;
          return (
            <Tab
              role='tab'
              id={`tab-${value}`}
              key={value}
              aria-controls={`tabpanel-${value}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              selected={selected}
              error={hasError}
              onClick={() => onTabChange(value)}
              onKeyDown={handleKeyDown}
            >
              <TabHeaderContent showEmbeddedSearch={showEmbeddedSearch}>
                <TabTextGroup gap={showFeeBadgeInline ? 2 : 1}>
                  <TabText
                    size='medium'
                    weight={selectedTab === value ? 'semi' : 'regular'}
                    selected={selectedTab === value}
                  >
                    {label}
                  </TabText>
                  {!!sublabel && (
                    <TabText size='medium' selected={selectedTab === value}>
                      {sublabel}
                    </TabText>
                  )}
                  {!!badgeLabel && showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} badgeIcon={badgeIcon} />}
                </TabTextGroup>
                {!!badgeLabel && !showFeeBadgeInline && <FeeBadge badgeLabel={badgeLabel} />}
              </TabHeaderContent>
            </Tab>
          );
        })}
      </TabHeader>
      <TabContent
        aria-labelledby={`tab-${selectedTab}`}
        id={`tabpanel-${selectedTab}`}
        role='tabpanel'
        selectedTab={selectedTab}
        isFirst={selectedTab === firstTabValue}
        error={tabOptions.some((tab) => tab.hasError)}
        showEmbeddedSearch={showEmbeddedSearch}
      >
        {children}
      </TabContent>
    </TabsContainer>
  );
};

export const TabsContainer = styled.div<{ showEmbeddedSearch: boolean }>`
  display: flex;
  width: 100%;
  min-width: 320px;
  max-width: ${({ showEmbeddedSearch }) => (showEmbeddedSearch ? EMBEDDED_MAX_WIDTH : BASE_MAX_WIDTH)}px;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[3]};
`;

export const TabHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-self: stretch;
  max-width: ${BASE_MAX_WIDTH}px;
`;

export const Tab = styled.div<{ selected: boolean; error?: boolean }>`
  ${({ theme: { spaces, customized }, selected, error }) => {
    const getBorder = (): string => {
      if (error) {
        return '2px solid #D34A4A';
      }
      if (selected) {
        return '2px solid #3387E1';
      }
      return `1px solid ${String(colors.grey[85])}`;
    };

    return css`
      display: flex;
      padding: ${spaces[3]};
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      align-content: space-between;
      flex: 1 0 0;
      height: 72px;
      border-radius: ${customized.radius.md};
      border: ${getBorder()};
      background: ${selected ? '#FFF' : '#F2F6F8'};
      cursor: pointer;
    `;
  }}
`;

export const TabHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[2]};
`;
const getTabContentBorder = (showEmbeddedSearch: boolean, error: boolean): string => {
  if (showEmbeddedSearch) {
    return 'none';
  }

  return error ? '1px solid #D34A4A' : `1px solid ${colors.grey[85]}`;
};
export const TabContent = styled.div<{ error?: boolean; showEmbeddedSearch?: boolean }>`
  ${({ theme: { customized }, error, showEmbeddedSearch }) => css`
    display: flex;
    min-height: 100px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: ${customized.radius.md};
    border: ${getTabContentBorder(showEmbeddedSearch, error)};
    background-color: ${showEmbeddedSearch ? 'transparent' : '#fff'};
    margin-left: ${showEmbeddedSearch ? `-${PLAID_OFFSET}px` : '0'};
  `}
`;
